/**
 * PWA runtime configuration.
 *
 * The install-time index.html ships with SocialPulse defaults. At runtime,
 * we detect the site slug and patch <link rel="manifest">, theme colour and
 * apple-touch-icon so each sister-app installs with its own brand.
 *
 * Also registers the service worker if REACT_APP_IFT_PWA=1 (or window is
 * running as an already-installed PWA).
 */

import { resolveSiteSlug } from "@/config/siteConfig";

const BRAND_META = {
  indianfamilytree: {
    manifest: "/manifest-indianfamilytree.json",
    themeColor: "#C2410C",
    backgroundColor: "#FFF7ED",
    appleTouchIcon: "/ift/icons/apple-touch-icon.png",
    favicon: "/ift/icons/favicon-64.png",
    title: "Indian Family Tree",
  },
  foodpulse: {
    manifest: "/manifest-foodpulse.json",
    themeColor: "#EA580C",
    backgroundColor: "#FFF7ED",
    appleTouchIcon: "/foodpulse/apple-touch-icon.png",
    favicon: "/foodpulse/favicon.png",
    title: "Food Pulse",
  },
  socialpulse: {
    manifest: "/manifest-socialpulse.json",
    themeColor: "#7C3AED",
    backgroundColor: "#FFFFFF",
    appleTouchIcon: "/assets/social-pulse-logo.jpg",
    favicon: "/assets/social-pulse-logo.jpg",
    title: "Social Pulse",
  },
};

function setLink(rel, href, opts = {}) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  if (opts.type) el.type = opts.type;
}

function setMeta(name, content, useProperty = false) {
  const sel = useProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (useProperty) el.setAttribute("property", name); else el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function applyBrandPwaMetadata() {
  try {
    const slug = resolveSiteSlug();
    const brand = BRAND_META[slug] || BRAND_META.socialpulse;
    setLink("manifest", brand.manifest);
    setLink("apple-touch-icon", brand.appleTouchIcon);
    setLink("icon", brand.favicon);
    setMeta("theme-color", brand.themeColor);
    setMeta("apple-mobile-web-app-capable", "yes");
    setMeta("apple-mobile-web-app-status-bar-style", "default");
    setMeta("apple-mobile-web-app-title", brand.title);
    setMeta("mobile-web-app-capable", "yes");
    setMeta("application-name", brand.title);
  } catch (e) {
    // never crash the app because of PWA metadata
    // eslint-disable-next-line no-console
    console.warn("applyBrandPwaMetadata failed", e);
  }
}

const PWA_FLAG = process.env.REACT_APP_IFT_PWA === "1";

function isStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

export function registerPwa() {
  if (typeof window === "undefined") return;
  applyBrandPwaMetadata();

  const shouldRegister = PWA_FLAG || isStandalone();
  if (!shouldRegister) return;
  if (!("serviceWorker" in navigator)) return;

  // Register on load to avoid contending with initial paint.
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" })
      .then((reg) => {
        // eslint-disable-next-line no-console
        console.info("[PWA] service worker registered", reg.scope);
        // Prompt to reload when a new SW takes over
        if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              // A new version is available — tell it to take over.
              try { reg.waiting && reg.waiting.postMessage("SKIP_WAITING"); } catch (_) {}
            }
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[PWA] service worker registration failed", err);
      });

    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      // Give React a moment to flush before hard reload
      setTimeout(() => window.location.reload(), 250);
    });
  });
}
